import { Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DdqSessionQuestion } from '@/types/api.types';
import { selectAutoAcceptable } from '../utils/autoAccept';

export interface AutoAcceptBarProps {
  questions: DdqSessionQuestion[];
  onAcceptAll: (questionIds: string[]) => void;
  isProcessing?: boolean;
  className?: string;
}

/**
 * The batch auto-accept lane. Surfaces the count of answers that are safe to
 * approve as a group — Exact-tier matches grounded in live vault data that have
 * not yet been actioned — so the reviewer's attention flows to the tail that
 * actually needs judgement. Hides itself when nothing is eligible.
 */
export function AutoAcceptBar({ questions, onAcceptAll, isProcessing, className }: AutoAcceptBarProps) {
  const eligible = selectAutoAcceptable(questions);

  if (eligible.length === 0) {
    return null;
  }

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900 dark:bg-emerald-950/40 ${className ?? ''}`}
    >
      <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200">
        <Zap className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          <span className="font-semibold">{eligible.length}</span> vault-grounded{' '}
          {eligible.length === 1 ? 'answer is' : 'answers are'} ready to auto-accept
        </span>
      </div>
      <Button
        size="sm"
        onClick={() => onAcceptAll(eligible.map((q) => q.question_id))}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" />
        ) : (
          <Zap className="h-4 w-4 mr-1.5" aria-hidden="true" />
        )}
        Accept all {eligible.length}
      </Button>
    </div>
  );
}
